import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import { IGalleryTab } from './model';

const styles = require('./tabs.css');

interface ITabsProps {
  imageIndex: number;
  tabs: IGalleryTab[];
  visible: boolean;
  onChange(tab: IGalleryTab): void;
}

export class Tabs extends React.Component<ITabsProps, {}> {
  private container: HTMLElement;
  private activeTab: HTMLElement;
  private activeIndex: number;

  public render() {
    const { tabs, imageIndex } = this.props;
    const containerStyles = mergeStyles(styles['container'], !this.props.visible && styles['invisible']);

    this.activeIndex = this.findTabIndexByImageIndex(imageIndex);

    if (!Array.isArray(tabs) || !tabs.length) {
      return null;
    }

    return (
      <div ref={this.setContainer} {...containerStyles}>
        {tabs.map((tab, i) => {
          const isCurrentTab = this.activeIndex === i;
          const tabStyles = mergeStyles(styles['tab'], isCurrentTab && styles['active']);

          return (
            <div
              {...tabStyles}
              onClick={() => this.setTab(i)}
              ref={isCurrentTab ? this.setActiveTab : undefined}
              key={i}
            >
              {tab.title}
            </div>
          );
        })}
      </div>
    );
  }

  private findTabIndexByImageIndex(imageIndex: number): number {
    const { tabs } = this.props;

    if (!Array.isArray(tabs) || !tabs.length) {
      return -1;
    }

    return tabs.findIndex((currentTab, index) => {
      const nextTab = tabs[index + 1];
      const nextStartIndex = nextTab ? nextTab.startIndex : +Infinity;

      return currentTab.startIndex <= imageIndex && imageIndex < nextStartIndex;
    });
  }

  private setTab(index: number) {
    if (this.activeIndex !== index) {
      this.props.onChange(this.props.tabs[index]);
    }
  }

  private setContainer = (node: HTMLElement | null) => {
    if (!node) {
      return;
    }

    this.container = node;
    this.scrollWithOffsetX(0);
  };

  private setActiveTab = (node: HTMLElement | null) => {
    if (!node) {
      return;
    }

    this.activeTab = node;
    this.smoothScrollToCurrentTab();
  };

  private smoothScrollToCurrentTab() {
    if (!window.requestAnimationFrame) {
      this.scrollWithOffsetX(0);

      return;
    }

    const { activeTab, container } = this;
    if (!container || !activeTab) {
      return;
    }

    const scrollWithoutOffset = this.getScrollWithoutOffset();
    const pathLength = container.scrollLeft - scrollWithoutOffset;
    let startTime: number;

    function scroll(time: number) {
      if (!startTime) {
        startTime = time;
        requestAnimationFrame(scroll);

        return;
      }

      const done = (time - startTime) / 200;

      if (done < 1) {
        container.scrollLeft = scrollWithoutOffset + pathLength * (1 - done);
        requestAnimationFrame(scroll);
      } else {
        container.scrollLeft = scrollWithoutOffset;
      }
    }

    requestAnimationFrame(scroll);
  }

  private scrollWithOffsetX(offsetX: number) {
    if (!this.container || !this.activeTab) {
      return;
    }

    this.container.scrollLeft = this.getScrollWithoutOffset() + this.normalizeOffset(offsetX);
  }

  private normalizeOffset(offsetX: number) {
    return (offsetX * this.activeTab.clientWidth) / window.innerWidth;
  }

  private getScrollWithoutOffset() {
    return this.activeTab.offsetLeft + this.activeTab.clientWidth / 2 - window.innerWidth / 2;
  }
}
